import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { xmlTerceirosApi } from '../api/xmlTerceiros';
import type { XmlTerceirosFilters, ExportOptions, XmlTerceirosStats } from '../types/xmlTerceiros';
import { logError } from '../utils/clientLog';

// Keys para cache do React Query
export const xmlTerceirosKeys = {
  all: ['xmlTerceiros'] as const,
  lists: () => [...xmlTerceirosKeys.all, 'list'] as const,
  list: (filters: XmlTerceirosFilters = {}) => [...xmlTerceirosKeys.lists(), filters] as const,
  stats: () => [...xmlTerceirosKeys.all, 'stats'] as const,
  parcelas: (nfeId: number) => [...xmlTerceirosKeys.all, 'parcelas', nfeId] as const,
  erpPagamento: (nfeId: number) => [...xmlTerceirosKeys.all, 'erp-pagamento', nfeId] as const,
};

// Hook principal para buscar XML de terceiros
export function useXmlTerceiros(filters: XmlTerceirosFilters = {}, page = 1, pageSize = 50) {
  return useQuery({
    queryKey: [...xmlTerceirosKeys.list(filters), page, pageSize],
    queryFn: () => xmlTerceirosApi.getXmlTerceiros(filters, page, pageSize),
    select: (response) => response.data,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchInterval: 30000,
  });
}

// Hook para estatísticas dos XML de terceiros
export function useXmlTerceirosStats(filters: XmlTerceirosFilters = {}) {
  const { data: response, ...rest } = useXmlTerceiros(filters, 1, 1000); // Buscar mais dados para estatísticas
  
  const xmlTerceiros = response?.data || [];
  const stats: XmlTerceirosStats = {
    total: response?.summary?.totalXmls || xmlTerceiros.length || 0,
    pendentes: xmlTerceiros.filter(item => item.pendente === true).length || 0,
    processados: xmlTerceiros.filter(item => item.pendente === false).length || 0,
    valorTotal: response?.summary?.totalValue || xmlTerceiros.reduce((sum, item) => sum + item.valorTotalNf, 0) || 0,
    ultimaAtualizacao: new Date().toISOString(),
  };

  return { data: stats, ...rest };
}

// Hook para buscar parcelas/fatura/pagamentos extraídos do XML (lazy — só busca quando enabled=true)
export function useParcelasNfe(nfeId: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: nfeId ? xmlTerceirosKeys.parcelas(nfeId) : ['xmlTerceiros', 'parcelas', 'disabled'],
    queryFn: () => xmlTerceirosApi.getParcelas(nfeId!),
    select: (response) => response.data.data,
    enabled: !!nfeId && enabled,
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  });
}

// Hook para consultar situação da NFe no ERP (contas a pagar)
export function useErpPagamentoNfe(nfeId: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: nfeId ? xmlTerceirosKeys.erpPagamento(nfeId) : ['xmlTerceiros', 'erp-pagamento', 'disabled'],
    queryFn: () => xmlTerceirosApi.getErpPagamento(nfeId!),
    select: (response) => response.data.data,
    enabled: !!nfeId && enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

// Hook para baixar DANFE
export function useBaixarDanfe() {
  return useMutation({
    mutationFn: (nfeId: number) => xmlTerceirosApi.baixarDanfe(nfeId),
    onError: (error: Error) => {
      logError('Erro ao baixar DANFE:', error);
    },
  });
}

// Hook para baixar XML
export function useBaixarXml() {
  return useMutation({
    mutationFn: (nfeId: number) => xmlTerceirosApi.baixarXml(nfeId),
    onError: (error: Error) => {
      logError('Erro ao baixar XML:', error);
    },
  });
}

// Hook para exportar arquivos (genérico)
export function useExportarArquivo() {
  return useMutation({
    mutationFn: (options: ExportOptions) => xmlTerceirosApi.exportarArquivo(options),
    onError: (error: Error, variables) => {
      console.error(`Erro ao baixar ${variables.tipo.toUpperCase()}:`, error);
    },
  });
}

// Hook para invalidar cache (útil após operações que afetam os dados)
export function useInvalidateXmlTerceiros() {
  const queryClient = useQueryClient();
  
  return () => {
    queryClient.invalidateQueries({ queryKey: xmlTerceirosKeys.all });
  };
}